import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  Copy,
  Download,
  Film,
  ArrowUpCircle,
  X,
  Loader2,
  ChevronDown,
  Pencil,
  RefreshCw,
  Share2,
  Pin,
  PinOff,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import type { GalleryItem, GalleryCategory } from "./types";
import { GALLERY_CATEGORIES, GALLERY_CATEGORY_LABEL_KEYS } from "./types";

interface AssetDetailModalProps {
  item: GalleryItem | null;
  items?: GalleryItem[];
  onClose: () => void;
  onNavigate?: (item: GalleryItem) => void;
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatBytes(bytes: number): string {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
}

export default function AssetDetailModal({
  item,
  items = [],
  onClose,
  onNavigate,
}: AssetDetailModalProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [isDownloadingWatermarked, setIsDownloadingWatermarked] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement | null>(null);

  const currentIndex = items.findIndex((i) => i.id === item?.id);

  const navigate = useCallback(
    (dir: "prev" | "next") => {
      if (!onNavigate) return;
      const idx = dir === "prev" ? currentIndex - 1 : currentIndex + 1;
      if (items[idx]) onNavigate(items[idx]);
    },
    [currentIndex, items, onNavigate]
  );

  useEffect(() => {
    if (!item) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) navigate("prev");
      if (e.key === "ArrowRight" && currentIndex < items.length - 1) navigate("next");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [item, currentIndex, items.length, navigate, onClose]);

  useEffect(() => {
    setPromptExpanded(false);
    setInfoExpanded(false);
    setDownloadMenuOpen(false);
  }, [item?.id]);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!downloadMenuRef.current) return;
      if (!downloadMenuRef.current.contains(e.target as Node)) setDownloadMenuOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, [downloadMenuOpen]);

  const upscaleMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/viral-gallery/${id}/upscale`),
    onSuccess: () => {
      toast({ title: t("viralGallery.detail.upscaleStarted") });
      queryClient.invalidateQueries({ queryKey: ["viral-gallery"] });
    },
    onError: (err: Error) =>
      toast({
        title: t("viralGallery.detail.error"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const toVideoMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/viral-gallery/${id}/to-video`),
    onSuccess: () => {
      toast({ title: t("viralGallery.detail.videoStarted") });
      queryClient.invalidateQueries({ queryKey: ["viral-gallery"] });
    },
    onError: (err: Error) =>
      toast({
        title: t("viralGallery.detail.error"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const { user } = useAuth();
  const isSuperAdmin = (user as { role?: string } | null | undefined)?.role === "super_admin";

  const pinMutation = useMutation({
    mutationFn: ({ id, category, pin }: { id: string; category: GalleryCategory; pin: boolean }) =>
      apiRequest("POST", `/api/viral-gallery/${id}/pin`, { category, pin }),
    onSuccess: () => {
      toast({ title: t("viralGallery.detail.adminUpdated") });
      queryClient.invalidateQueries({ queryKey: ["viral-gallery"] });
    },
    onError: (err: Error) =>
      toast({
        title: t("viralGallery.detail.error"),
        description: err.message,
        variant: "destructive",
      }),
  });

  if (!item) return null;

  const isVideo = item.mediaType === "video";
  const authorInitial = (item.authorName || "" || "V")[0].toUpperCase();
  const prompt = item.prompt ?? "";

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast({ title: t("viralGallery.detail.promptCopied") });
    } catch {
      toast({ title: t("viralGallery.detail.copyFailed"), variant: "destructive" });
    }
  };

  const download = () => {
    if (!item.signedUrl) return;
    const a = document.createElement("a");
    a.href = item.signedUrl;
    a.download = item.filename;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setDownloadMenuOpen(false);
  };

  const downloadWithWatermark = async () => {
    if (isDownloadingWatermarked) return;
    setIsDownloadingWatermarked(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/viral-gallery/${item.id}/download?watermark=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `watermarked-${item.filename}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloadMenuOpen(false);
    } catch (err) {
      toast({
        title: t("viralGallery.detail.watermarkedDownloadFailed"),
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setIsDownloadingWatermarked(false);
    }
  };

  const recreate = () => {
    const s = (item.creatorSettings as Record<string, unknown> | null) ?? {};
    const params = new URLSearchParams();

    const creatorRouteMap: Record<string, () => void> = {
      "ugc-studio": () => {
        if (s.format) params.set("format", String(s.format));
        if (s.aspectRatio) params.set("aspect", String(s.aspectRatio));
        if (s.resolution) params.set("resolution", String(s.resolution));
        if (s.durationSeconds) params.set("duration", String(s.durationSeconds));
        if (s.avatarId) params.set("avatarId", String(s.avatarId));
        if (s.productId) params.set("productId", String(s.productId));
        if (s.audioMode) params.set("audioMode", String(s.audioMode));
        if (s.voiceId) params.set("voiceId", String(s.voiceId));
        if (prompt) params.set("prompt", prompt);
        setLocation(`/chat/ugc-studio?${params.toString()}`);
      },
      "ad-graphics": () => {
        // Deep-link to Kreator reklam (ad-creator graphic mode). We intentionally
        // skip `prompt` — the user shouldn't see the raw backend prompt; instead
        // they get the same layout + model preselected and just upload their
        // own brand reference photo.
        if (item.imageModel) params.set("model", item.imageModel);
        if (s.selectedLayout) params.set("layout", String(s.selectedLayout));
        if (s.contentType) params.set("contentType", String(s.contentType));
        params.set("mode", "graphic");
        setLocation(`/chat/ad-creator?${params.toString()}`);
      },
      "reel-creator": () => {
        if (prompt) params.set("prompt", prompt);
        if (item.imageModel) params.set("model", item.imageModel);
        setLocation(`/chat/reel-creator?${params.toString()}`);
      },
    };

    const creatorType = item.creatorType ?? (isVideo ? "reel-creator" : "ad-graphics");
    const handler =
      creatorRouteMap[creatorType] ?? creatorRouteMap[isVideo ? "reel-creator" : "ad-graphics"];
    handler();
    onClose();
  };

  const infoRows = [
    item.imageModel && { label: t("viralGallery.detail.model"), value: item.imageModel },
    { label: t("viralGallery.detail.dimensions"), value: `${item.width} × ${item.height}` },
    { label: t("viralGallery.detail.size"), value: formatBytes(item.fileSizeBytes) },
    { label: t("viralGallery.detail.format"), value: item.format },
    {
      label: t("viralGallery.detail.type"),
      value: isVideo ? t("viralGallery.detail.video") : t("viralGallery.detail.image"),
    },
    { label: t("viralGallery.detail.date"), value: formatDate(item.createdAt, i18n.language) },
    item.galleryCategory && {
      label: t("viralGallery.detail.category"),
      value: t(GALLERY_CATEGORY_LABEL_KEYS[item.galleryCategory]),
    },
  ].filter(Boolean) as { label: string; value: string }[];

  return createPortal(
    /* Full-screen overlay */
    <div className="fixed inset-0 z-[9999] flex">
      {/* Blurred bg from current image */}
      {item.signedUrl && !isVideo && (
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: `url(${item.signedUrl})`,
            filter: "blur(40px) brightness(0.25)",
          }}
        />
      )}
      {isVideo && <div className="absolute inset-0 bg-black" />}

      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-[356px] z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Prev / Next */}
      {currentIndex > 0 && (
        <button
          onClick={() => navigate("prev")}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          ‹
        </button>
      )}
      {currentIndex < items.length - 1 && (
        <button
          onClick={() => navigate("next")}
          className="absolute right-[376px] top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        >
          ›
        </button>
      )}

      {/* Left: image area — click outside panel to close */}
      <div
        className="relative flex-1 flex items-center justify-center p-8 cursor-default"
        onClick={onClose}
      >
        {item.signedUrl ? (
          isVideo ? (
            <video
              key={item.id}
              src={item.signedUrl}
              controls
              autoPlay
              loop
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              key={item.id}
              src={item.signedUrl}
              alt={item.filename}
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )
        ) : (
          <div className="text-white/30">{t("viralGallery.detail.noPreview")}</div>
        )}
      </div>

      {/* Right panel */}
      <div className="relative z-10 w-[360px] shrink-0 bg-[#111] flex flex-col h-full overflow-hidden">
        {/* Author header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {authorInitial}
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">
                {item.authorName ?? "Viral Partners"}
              </p>
              <p className="text-white/40 text-[11px]">{t("viralGallery.detail.author")}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/60 flex items-center justify-center transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Prompt section */}
          <div className="px-5 py-4 border-b border-white/[0.07]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-white/60 text-[11px] font-semibold uppercase tracking-widest">
                <span className="w-3.5 h-3.5 inline-flex items-center justify-center opacity-60">
                  ✦
                </span>
                {t("viralGallery.detail.prompt")}
              </div>
              <button
                onClick={copyPrompt}
                disabled={!prompt}
                className="text-[11px] font-medium text-white/60 hover:text-white border border-white/[0.12] hover:border-white/30 rounded-md px-2.5 py-1 transition-colors disabled:opacity-30"
              >
                {t("viralGallery.detail.copy")}
              </button>
            </div>
            {prompt ? (
              <>
                <p
                  className={`text-white/80 text-[13px] leading-relaxed ${promptExpanded ? "" : "line-clamp-4"}`}
                >
                  {prompt}
                </p>
                {prompt.length > 180 && (
                  <button
                    onClick={() => setPromptExpanded((v) => !v)}
                    className="mt-2 flex items-center gap-1 text-white/40 hover:text-white/70 text-[11px] transition-colors"
                  >
                    {promptExpanded
                      ? t("viralGallery.detail.collapse")
                      : t("viralGallery.detail.seeAll")}
                    <ChevronDown
                      className={`w-3 h-3 transition-transform ${promptExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
              </>
            ) : (
              <p className="text-white/30 text-xs italic">{t("viralGallery.detail.noPrompt")}</p>
            )}
          </div>

          {/* Information section */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-white/60 text-[11px] font-semibold uppercase tracking-widest">
                <span className="opacity-60">ℹ</span>
                {t("viralGallery.detail.information")}
              </div>
            </div>
            <div className="space-y-2.5">
              {(infoExpanded ? infoRows : infoRows.slice(0, 3)).map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-3">
                  <span className="text-white/40 text-xs">{label}</span>
                  <span className="text-white/85 text-xs font-medium text-right break-all">
                    {value}
                  </span>
                </div>
              ))}
            </div>
            {infoRows.length > 3 && (
              <button
                onClick={() => setInfoExpanded((v) => !v)}
                className="mt-3 flex items-center gap-1 text-white/40 hover:text-white/70 text-[11px] transition-colors"
              >
                {infoExpanded ? t("viralGallery.detail.less") : t("viralGallery.detail.seeAll")}
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${infoExpanded ? "rotate-180" : ""}`}
                />
              </button>
            )}
          </div>

          {/* Superadmin: gallery curation */}
          {isSuperAdmin && (
            <div className="px-5 py-4 border-t border-white/[0.07] bg-white/[0.02]">
              <div className="flex items-center gap-2 text-amber-300/80 text-[11px] font-semibold uppercase tracking-widest mb-3">
                <span className="opacity-80">★</span>
                {t("viralGallery.detail.adminTitle")}
              </div>
              <label className="block text-white/50 text-[11px] mb-1.5">
                {t("viralGallery.detail.adminCategoryLabel")}
              </label>
              <select
                value={item.galleryCategory ?? ""}
                disabled={pinMutation.isPending}
                onChange={(e) => {
                  const next = e.target.value as GalleryCategory | "";
                  if (!next) {
                    if (item.isInGallery) {
                      pinMutation.mutate({
                        id: item.id,
                        category: (item.galleryCategory ??
                          GALLERY_CATEGORIES[0]) as GalleryCategory,
                        pin: false,
                      });
                    }
                    return;
                  }
                  pinMutation.mutate({ id: item.id, category: next, pin: true });
                }}
                className="w-full bg-[#1a1a1a] border border-white/[0.12] text-white/90 text-[12.5px] rounded-md px-2.5 py-2 focus:outline-none focus:border-white/30 disabled:opacity-50"
              >
                <option value="">{t("viralGallery.detail.adminCategoryNone")}</option>
                {GALLERY_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(GALLERY_CATEGORY_LABEL_KEYS[c])}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-white/40">
                {item.isInGallery ? (
                  <>
                    <Pin className="w-3 h-3 text-emerald-400" />
                    {t("viralGallery.detail.adminPinned")}
                  </>
                ) : (
                  <>
                    <PinOff className="w-3 h-3 text-white/30" />
                    {t("viralGallery.detail.adminNotPinned")}
                  </>
                )}
                {pinMutation.isPending && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-white/[0.07] space-y-2.5">
          {/* Recreate — brand color */}
          <button
            onClick={recreate}
            className="w-full h-12 rounded-xl bg-[#ef3e29] hover:bg-[#d9341f] text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t("viralGallery.detail.recreate")}
          </button>

          {/* 2×2 action grid */}
          <div className="grid grid-cols-2 gap-2">
            <ActionBtn
              icon={<Film className="w-3.5 h-3.5" />}
              label={t("viralGallery.detail.createVideo")}
              onClick={() => toVideoMutation.mutate(item.id)}
              disabled={toVideoMutation.isPending || isVideo}
              loading={toVideoMutation.isPending}
            />
            <div ref={downloadMenuRef} className="relative">
              <ActionBtn
                icon={
                  isDownloadingWatermarked ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )
                }
                label={t("viralGallery.detail.download")}
                onClick={() => setDownloadMenuOpen((v) => !v)}
                disabled={!item.signedUrl || isDownloadingWatermarked}
              />
              {downloadMenuOpen && (
                <div className="absolute right-0 bottom-full mb-2 w-56 z-20 rounded-lg bg-[#1a1a1a] border border-white/[0.12] shadow-2xl overflow-hidden">
                  <button
                    onClick={download}
                    disabled={!item.signedUrl}
                    className="w-full text-left px-3 py-2.5 text-[12.5px] text-white/85 hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Download className="w-3.5 h-3.5 opacity-70" />
                    {t("viralGallery.detail.downloadOriginal")}
                  </button>
                  <div className="h-px bg-white/[0.08]" />
                  <button
                    onClick={downloadWithWatermark}
                    disabled={!item.signedUrl || isDownloadingWatermarked}
                    className="w-full text-left px-3 py-2.5 text-[12.5px] text-white/85 hover:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDownloadingWatermarked ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin opacity-70" />
                    ) : (
                      <Download className="w-3.5 h-3.5 opacity-70" />
                    )}
                    {t("viralGallery.detail.downloadWatermarked")}
                  </button>
                </div>
              )}
            </div>
            <ActionBtn
              icon={<ArrowUpCircle className="w-3.5 h-3.5" />}
              label={t("viralGallery.detail.upscale")}
              onClick={() => upscaleMutation.mutate(item.id)}
              disabled={upscaleMutation.isPending || isVideo}
              loading={upscaleMutation.isPending}
            />
            <ActionBtn
              icon={<Pencil className="w-3.5 h-3.5" />}
              label={t("viralGallery.detail.edit")}
              onClick={recreate}
              disabled={!prompt}
            />
          </div>

          {item.isOwn && (
            <ActionBtn
              icon={<Share2 className="w-3.5 h-3.5" />}
              label={
                item.isPublic
                  ? t("viralGallery.detail.removeFromExplore")
                  : t("viralGallery.detail.share")
              }
              onClick={() => {}} // share handled elsewhere
              fullWidth
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ActionBtn({
  icon,
  label,
  onClick,
  disabled,
  loading,
  fullWidth,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${fullWidth ? "col-span-2" : ""} flex items-center justify-center gap-2 h-10 rounded-xl bg-white/[0.07] hover:bg-white/[0.13] text-white/80 hover:text-white text-xs font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-white/[0.07]`}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}
      {label}
    </button>
  );
}
